import { type TextAlignment } from '@canvas/adf-types';
import {
  type Element,
  type ElementData,
  type ElementDataWithContent,
  type ElementType,
  elementType,
} from '@canvas/model/element';
import { type AnyElement, type AnyElementData } from '@canvas/model/elements/any';
import { type ElementClipboardData } from '@canvas/model/ui';
import { type Vector2, type Vector3 } from '@canvas/vector-types';
export const textElementType = elementType('text');

export function isText(element: AnyElement | undefined): element is TextElement {
  return !!element && isTextType(element.type);
}

export interface TextElementData extends ElementDataWithContent<ElementType> {
  text: string;
  allowFlexibleWidth: boolean;
  color: Vector3;
  fontScale?: number;
  /**
   * @see https://hello.atlassian.net/wiki/spaces/CAN/pages/2869987222 to know more about what basis sizing is
   */
  basisSize?: Vector2;
  /**
   * @see https://hello.atlassian.net/wiki/spaces/CAN/pages/2869987222 to know more about what basis sizing is
   */
  basisPosition?: Vector2;
  alignment?: TextAlignment;
  rotation?: number;
  /**
   * @deprecated Use basisPosition instead
   * @see https://hello.atlassian.net/wiki/spaces/CAN/pages/2213850025/Diving+into+the+current+and+proposed+state+of+text+sizing+Ft.+GIFs+and+diagrams#So-what%E2%80%99s-the-solution%3F
   */
  position: ElementData['position'];
  /**
   * @deprecated Use basisSize instead
   * @see https://hello.atlassian.net/wiki/spaces/CAN/pages/2213850025/Diving+into+the+current+and+proposed+state+of+text+sizing+Ft.+GIFs+and+diagrams#So-what%E2%80%99s-the-solution%3F
   */
  size: ElementData['size'];
  /**
   * The ID of the composite root that this text element is a part of.
   * If not provided, the text element is not part of a composite shape.
   */
  compositeRootId?: string;
}

export interface TextElement extends TextElementData, Element<ElementType> {
  toJSON(): TextElementData;
}

export function isTextData(
  element: AnyElementData | undefined,
): element is TextElementData {
  return element?.type === textElementType;
}

export function isTextClipboardData(
  element: ElementClipboardData | undefined,
): element is Omit<TextElementData, 'id'> {
  return element?.type === textElementType;
}

export type CompositeTextElementData = TextElementData & { compositeRootId: string };

export interface CompositeTextElementPasteData extends TextElementData {
  compositeRootIndex: number;
}

export function isCompositeTextData(
  element: AnyElementData | undefined,
): element is CompositeTextElementData {
  return isTextData(element) && typeof element.compositeRootId === 'string';
}

export const isTextType = (elementType: ElementType | undefined): boolean => {
  return !!elementType && elementType === textElementType;
};
